import {
  addEventListenerHashParamJson,
  setHashParamValueJsonInWindow,
} from "@metapages/hash-query";
import { HashParamDefinition } from "@metapages/metapage";

import {
  CanvasBoundsType,
  CombinedResults,
  DirectionControllerInputName,
  DirectionControllerPayload,
  SuperslidesMenuRequest,
  TapDirection,
} from "/@/shared/motion-menu-system/MenuTypes";
import type {
  ControllerOptionsConfig,
  MotionController,
  MotionControllerParameters,
} from "./types.ts";

export const DefaultDirectionalControllerGlyphOptions: Record<
  string,
  HashParamDefinition
> = {
  enable: {
    type: "boolean",
    default: { value: false },
    label: "Enable",
  },
  disableRendering: {
    type: "boolean",
    default: { value: false },
    label: "Hide Rendering",
  },
  navigationMode: {
    type: "string",
    default: { value: "both" },
    allowed: [
      { value: "both", label: "Both (navigate + output)" },
      { value: "controller", label: "Controller (navigate slides)" },
      { value: "output", label: "Output only (no navigation)" },
    ],
    label: "Navigation Mode",
  },
  horizontalStartLabel: {
    type: "string",
    default: { value: "Left" },
    label: "Horizontal Start Label",
  },
  horizontalEndLabel: {
    type: "string",
    default: { value: "Right" },
    label: "Horizontal End Label",
  },
  verticalStartLabel: {
    type: "string",
    default: { value: "Forward" },
    label: "Vertical Start Label",
  },
  verticalEndLabel: {
    type: "string",
    default: { value: "Back" },
    label: "Vertical End Label",
  },
};

const optionsKey = "directionalGlyph";
export const controllerOptionsConfig: ControllerOptionsConfig = {
  key: optionsKey,
  label: "Directional Glyph",
  optionDefinitions: DefaultDirectionalControllerGlyphOptions,
};

type DirectionalGlyphOptions = {
  enable?: boolean;
  enableHorizontal?: boolean;
  enableVertical?: boolean;
  disableRendering?: boolean;
  navigationMode?: "controller" | "output" | "both";
  horizontalStartLabel?: string;
  horizontalEndLabel?: string;
  verticalStartLabel?: string;
  verticalEndLabel?: string;
};

type DirectionalGlyphInstanceOptions = {
  hand: "left" | "right";
  orientation: "horizontal" | "vertical";
};

export class DirectionalControllerGlyphController implements MotionController {
  private options: DirectionalGlyphOptions = {};
  private disposers: (() => void)[] = [];
  private params: MotionControllerParameters;
  private hand: "left" | "right";
  private orientation: "horizontal" | "vertical";

  // DOM
  private root: HTMLDivElement;
  private overlay: HTMLDivElement;
  private effectDiv: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext;
  private U: Record<string, WebGLUniformLocation | null> = {};

  // Shader params
  private PARAMS: Record<string, number | null> = {
    effectX: null,
    effectY: null,
    effectSize: null,
    effectViewportX: null,
    effectViewportY: null,
    circleRadius: 0.85,
    circleSpeed: 0.4,
    circleLineWidth: 0.0002,
    circleGlowWidth: 0.002,
    circleGlowIntensity: 0.1,
    squareSize: 0.42,
    squareLineWidth: 0.0003,
    staticSquareLineWidth: 0.0002,
    squareGlowWidth: 0.0015,
    squareGlowIntensity: 0.08,
    innerCircleRadius: 0.15,
    innerCircleLineWidth: 0.0002,
    innerCircleGlowWidth: 0.0015,
    patternCircleRadius: 0.65,
    patternCircleLineWidth: 0.0002,
    patternCircleGlowWidth: 0.0015,
    glowSharpness: 2.0,
    alphaMultiplier: 0.9,
  };

  // Rotation state
  private readonly SQUARE_BASE_OFFSET = Math.PI / 4;
  private readonly ROTATION_THRESHOLD = Math.PI / 2;
  private readonly ROTATION_TOLERANCE = 0.1;
  private squareRotation = Math.PI / 4;
  private wristRotationRight = 0;
  private wristRotationLeft = 0;
  private activeHand: "left" | "right" | null = null;
  private baseRotation = 0;
  private isCompleted = false;
  private isAnimating = false;
  private flashIntensity = 0;
  private animationStartTime = 0;
  private arrowPointsRight: boolean;

  constructor(
    params: MotionControllerParameters,
    instanceOptions: DirectionalGlyphInstanceOptions,
  ) {
    this.params = params;
    const { iostream, root: parentRoot } = params;
    this.hand = instanceOptions.hand;
    this.orientation = instanceOptions.orientation;

    if (!iostream) {
      throw new Error("iostream is required");
    }
    if (parentRoot === undefined) {
      throw new Error("root is required");
    }

    this.arrowPointsRight = this.orientation === "horizontal"
      ? this.hand === "left"
      : this.hand === "right";

    // Create DOM structure
    this.root = document.createElement("div");
    this.root.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;";

    this.overlay = document.createElement("div");
    this.overlay.style.cssText = `
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  overflow: hidden;
`;
    this.root.appendChild(this.overlay);

    this.effectDiv = document.createElement("div");
    this.effectDiv.style.cssText = `
  position: absolute;
  pointer-events: none;
  display: none;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  ${this.orientation === "vertical" ? "transform: rotate(-90deg);" : ""}
`;
    this.overlay.appendChild(this.effectDiv);

    this.canvas = document.createElement("canvas");
    this.canvas.style.cssText = "width:100%;height:100%;display:block;";
    this.effectDiv.appendChild(this.canvas);

    const gl = this.canvas.getContext("webgl", {
      premultipliedAlpha: false,
      alpha: true,
    })!;
    if (gl === null) {
      throw new Error("Failed to get WebGL context");
    }
    this.gl = gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    this.setupShaders();

    // Listen for hash param options
    this.disposers.push(addEventListenerHashParamJson(
      optionsKey,
      (opts: DirectionalGlyphOptions | undefined) => {
        const prev = this.options;
        this.options = (opts ?? {}) as DirectionalGlyphOptions;

        // Default must match isEnabled (?? false) — otherwise a previously
        // "unset" state ({}) reads as enabled, so the disabled→enabled
        // transition is missed and the root is never re-attached.
        const prevEnabled = this.orientation === "horizontal"
          ? (prev.enableHorizontal ?? prev.enable ?? false)
          : (prev.enableVertical ?? prev.enable ?? false);
        const nowEnabled = this.isEnabled;

        if (prevEnabled && !nowEnabled) {
          this.root.remove();
        } else if (!prevEnabled && nowEnabled) {
          parentRoot.appendChild(this.root);
        }

        // Hide glyph immediately when rendering is disabled
        if (this.disableRendering) {
          this.effectDiv.style.display = "none";
          this.effectDiv.style.opacity = "0";
        }
      },
    ));

    // listen for options changing from metaframe inputs
    this.disposers.push(
      this.params.iostream.onInputs(
        (inputs) => {
          if (inputs[optionsKey]) {
            setHashParamValueJsonInWindow(
              optionsKey,
              inputs[optionsKey] as DirectionalGlyphOptions,
            );
          }
        },
      ),
    );

    // Attach rendering root (enabled by default)
    parentRoot.appendChild(this.root);

    // Start render loop
    requestAnimationFrame((t) => this.render(t));
  }

  private setupShaders(): void {
    const gl = this.gl;

    const vsSource = `
  attribute vec2 aPos;
  void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

    const fsSource = `
  precision highp float;

  uniform vec2  uResolution;
  uniform float uSquareRotation;
  uniform float uFlashIntensity;

  uniform float uCircleRadius;
  uniform float uCircleLineWidth;
  uniform float uCircleGlowWidth;
  uniform float uCircleGlowIntensity;

  uniform float uSquareSize;
  uniform float uSquareLineWidth;
  uniform float uStaticSquareLineWidth;
  uniform float uSquareGlowWidth;
  uniform float uSquareGlowIntensity;

  uniform float uInnerRadius;
  uniform float uInnerLineWidth;
  uniform float uInnerGlowWidth;

  uniform float uPatternRadius;
  uniform float uPatternLineWidth;
  uniform float uPatternGlowWidth;

  uniform float uGlowSharpness;
  uniform float uAlphaMultiplier;
  uniform float uArrowPointsRight;

  float circleOutline(vec2 p, float r) {
    return abs(length(p) - r);
  }

  float squareOutline(vec2 p, float size, float rot) {
    float c = cos(rot), s = sin(rot);
    vec2 q = vec2(c*p.x + s*p.y, -s*p.x + c*p.y);
    vec2 d = abs(q) - vec2(size);
    return abs(length(max(d, 0.0)) + min(max(d.x, d.y), 0.0));
  }

  float glowLine(float d, float lineW, float glowW, float glowI, float sharpness) {
    float core  = sharpness * lineW  / (d*d + lineW);
    float bloom = glowI     * glowW  / (d*d + glowW);
    return core + bloom;
  }

  float lineSegment(vec2 p, vec2 a, vec2 b, float w) {
    vec2 pa = p - a, ba = b - a;
    float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * h) - w;
  }

  float arrow(vec2 p, float size, float rot, float pointsRight) {
    float c = cos(rot), s = sin(rot);
    vec2 q = vec2(c*p.x + s*p.y, -s*p.x + c*p.y);

    vec2 center = vec2(0.0);
    vec2 targetCorner = pointsRight > 0.5 ?
      vec2(size, size) :
      vec2(-size, size);

    float shaft = lineSegment(q, center, targetCorner * 0.7, 0.004);

    vec2 tipEnd = targetCorner;

    vec2 headDir = normalize(targetCorner);
    vec2 headPerp = vec2(-headDir.y, headDir.x);

    vec2 tipLeft = tipEnd - headDir * 0.12 + headPerp * 0.06;
    vec2 tipRight = tipEnd - headDir * 0.12 - headPerp * 0.06;

    float head1 = lineSegment(q, tipEnd, tipLeft, 0.004);
    float head2 = lineSegment(q, tipEnd, tipRight, 0.004);

    return min(shaft, min(head1, head2));
  }

  float patternCircle(vec2 p, float radius, float rot) {
    float angle = atan(p.y, p.x);
    float dist = length(p);
    float circleDist = circleOutline(p, radius);

    float c = cos(rot), s = sin(rot);
    vec2 q = vec2(c*p.x + s*p.y, -s*p.x + c*p.y);
    float rotAngle = atan(q.y, q.x);

    float dotPattern = 1.0;
    for (float i = 0.0; i < 8.0; i++) {
      float targetAngle = i * 3.14159 * 2.0 / 8.0;
      float angleDiff = abs(mod(rotAngle - targetAngle + 3.14159, 6.28318) - 3.14159);

      if (angleDiff < 0.15) {
        vec2 dotPos = vec2(cos(targetAngle), sin(targetAngle)) * radius;
        float dotDist = length(q - dotPos);
        dotPattern = min(dotPattern, dotDist);
      }
    }

    return min(circleDist, dotPattern - 0.015);
  }

  void main() {
    vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
    uv *= 2.2;

    vec3 col = vec3(0.0);

    float cDist = circleOutline(uv, uCircleRadius);
    float cVal  = glowLine(cDist, uCircleLineWidth, uCircleGlowWidth, uCircleGlowIntensity, uGlowSharpness);
    col += vec3(1.0, 0.55, 0.1) * cVal * (1.0 + uFlashIntensity * 3.0);

    float pDist = patternCircle(uv, uPatternRadius, uSquareRotation);
    float pVal  = glowLine(pDist, uPatternLineWidth, uPatternGlowWidth, 0.08, uGlowSharpness);
    col += vec3(1.0, 0.65, 0.15) * pVal;

    float sq1d  = squareOutline(uv, uSquareSize, 0.0);
    float sq1v  = glowLine(sq1d, uStaticSquareLineWidth, uSquareGlowWidth, uSquareGlowIntensity, uGlowSharpness);
    col += vec3(1.0, 0.85, 0.2) * sq1v;

    float sq2d  = squareOutline(uv, uSquareSize, uSquareRotation);
    float sq2v  = glowLine(sq2d, uSquareLineWidth, uSquareGlowWidth, uSquareGlowIntensity, uGlowSharpness);
    col += vec3(1.0, 0.60, 0.1) * sq2v;

    float arrowDist = arrow(uv, uSquareSize, uSquareRotation, uArrowPointsRight);
    float arrowVal = glowLine(arrowDist, 0.0007, 0.0025, 0.06, uGlowSharpness);
    col += vec3(1.0, 0.60, 0.1) * arrowVal;

    float id    = circleOutline(uv, uInnerRadius);
    float iv    = glowLine(id, uInnerLineWidth, uInnerGlowWidth, uGlowSharpness * 0.25, uGlowSharpness);
    col += vec3(1.0, 0.75, 0.2) * iv * (1.0 + uFlashIntensity * 3.0);

    col = min(col, vec3(1.0));

    float a = max(col.r, max(col.g, col.b));
    a = clamp(a * uAlphaMultiplier, 0.0, 1.0);

    gl_FragColor = vec4(col * a, a);
  }
`;

    function mkShader(type: number, src: string) {
      const sh = gl.createShader(type);
      if (sh === null) {
        throw new Error("Failed to create shader");
      }
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error("Shader error:", gl.getShaderInfoLog(sh));
      }
      return sh;
    }

    const prog = gl.createProgram();
    gl.attachShader(prog, mkShader(gl.VERTEX_SHADER, vsSource));
    gl.attachShader(prog, mkShader(gl.FRAGMENT_SHADER, fsSource));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, -1, 1, 1, -1, 1]),
      gl.STATIC_DRAW,
    );

    const posLoc = gl.getAttribLocation(prog, "aPos");
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(posLoc);

    [
      "uResolution",
      "uSquareRotation",
      "uFlashIntensity",
      "uCircleRadius",
      "uCircleLineWidth",
      "uCircleGlowWidth",
      "uCircleGlowIntensity",
      "uSquareSize",
      "uSquareLineWidth",
      "uStaticSquareLineWidth",
      "uSquareGlowWidth",
      "uSquareGlowIntensity",
      "uInnerRadius",
      "uInnerLineWidth",
      "uInnerGlowWidth",
      "uPatternRadius",
      "uPatternLineWidth",
      "uPatternGlowWidth",
      "uGlowSharpness",
      "uAlphaMultiplier",
      "uArrowPointsRight",
    ].forEach((n) => (this.U[n] = gl.getUniformLocation(prog, n)));
  }

  private get isEnabled(): boolean {
    // Per-axis enable takes priority over global enable.
    // Defaults to false (disabled) to match the `enable` option default — the
    // glyph must be explicitly turned on.
    if (this.orientation === "horizontal") {
      return this.options.enableHorizontal ?? this.options.enable ?? false;
    }
    return this.options.enableVertical ?? this.options.enable ?? false;
  }

  private get disableRendering(): boolean {
    return !this.isEnabled ||
      this.options.disableRendering === true;
  }

  private get output(): "controller" | "output" | "both" {
    return this.options.navigationMode ?? "both";
  }

  private get effectiveLabels(): { start: string; end: string } {
    if (this.orientation === "horizontal") {
      return {
        start: this.options.horizontalStartLabel ?? "Left",
        end: this.options.horizontalEndLabel ?? "Right",
      };
    }
    return {
      start: this.options.verticalStartLabel ?? "Forward",
      end: this.options.verticalEndLabel ?? "Back",
    };
  }

  private effectiveWristRotation(which: "left" | "right"): number {
    return which === "right" ? this.wristRotationRight : this.wristRotationLeft;
  }

  private sendDirection(direction: TapDirection): void {
    const request: SuperslidesMenuRequest = {
      SetDirection: {
        direction,
        timestamp: Date.now(),
      },
    };
    globalThis.sendSuperslidesMenuRequest?.(request);
  }

  private mapNormalizedBoxToLocalCoordinates(
    box: {
      x: number;
      y: number;
      width: number;
      height: number;
      z?: number;
      depth?: number;
    },
    canvasBounds?: CanvasBoundsType | undefined,
  ) {
    const rootRect = this.root.getBoundingClientRect();
    if (canvasBounds != null) {
      const localLeft = canvasBounds.renderedX - rootRect.left;
      const localTop = canvasBounds.renderedY - rootRect.top;
      return {
        x: localLeft + box.x * canvasBounds.renderedWidth,
        y: localTop + box.y * canvasBounds.renderedHeight,
        width: box.width * canvasBounds.renderedWidth,
        height: box.height * canvasBounds.renderedHeight,
        z: box.z,
        depth: box.depth,
      };
    }
    return {
      x: box.x * rootRect.width,
      y: box.y * rootRect.height,
      width: box.width * rootRect.width,
      height: box.height * rootRect.height,
      z: box.z,
      depth: box.depth,
    };
  }

  private updateLayout(): void {
    if (this.disableRendering) return;
    const cW = this.overlay.clientWidth || 500;
    const cH = this.overlay.clientHeight || 500;
    const sz = this.PARAMS.effectSize ?? Math.min(cW, cH);
    const viewportX = this.PARAMS.effectViewportX;
    const viewportY = this.PARAMS.effectViewportY;
    if (viewportX != null && viewportY != null) {
      this.effectDiv.style.position = "fixed";
      this.effectDiv.style.left = viewportX - sz / 2 + "px";
      this.effectDiv.style.top = viewportY - sz / 2 + "px";
    } else {
      this.effectDiv.style.position = "absolute";
      const cx = this.PARAMS.effectX ?? cW / 2;
      const cy = this.PARAMS.effectY ?? cH / 2;
      this.effectDiv.style.left = cx - sz / 2 + "px";
      this.effectDiv.style.top = cy - sz / 2 + "px";
    }
    this.effectDiv.style.width = sz + "px";
    this.effectDiv.style.height = sz + "px";
  }

  private resize(): void {
    if (this.disableRendering) return;
    this.updateLayout();
    const w = Math.round(this.effectDiv.clientWidth * devicePixelRatio);
    const h = Math.round(this.effectDiv.clientHeight * devicePixelRatio);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.gl.viewport(0, 0, w, h);
    }
  }

  private render(timestamp: number): void {
    // Direction detection always runs regardless of disableRendering
    if (!this.isCompleted && !this.isAnimating && this.activeHand) {
      const currentWristRotation = this.effectiveWristRotation(this.activeHand);
      // Wrap to [-π, π] (shortest angular path). calculateWristRotation returns a
      // raw atan2 in [-π, π] with no unwrapping, so a dorsal-facing fist sits near
      // the ±π seam. When the hand drifts off-screen (e.g. off the left edge) the
      // value can flip across the seam, which would otherwise read as a ~2π jump and
      // spuriously trip the threshold in one fixed direction. Wrapping the delta makes
      // a seam flip a no-op instead of a false navigation.
      const rotationDelta = Math.atan2(
        Math.sin(this.baseRotation - currentWristRotation),
        Math.cos(this.baseRotation - currentWristRotation),
      );

      if (
        Math.abs(rotationDelta) >=
          this.ROTATION_THRESHOLD - this.ROTATION_TOLERANCE
      ) {
        this.isCompleted = true;
        this.isAnimating = true;
        this.animationStartTime = timestamp;
        this.squareRotation = this.SQUARE_BASE_OFFSET +
          (rotationDelta > 0
            ? this.ROTATION_THRESHOLD
            : -this.ROTATION_THRESHOLD);
        const isRightOrForward = rotationDelta < 0;
        const effectiveLabels = this.effectiveLabels;

        if (this.orientation === "horizontal") {
          this.params.iostream.setOutputs({
            MomentaryToggleSwitchHorizontal: isRightOrForward ? 1 : -1,
          });
          if (this.output === "controller" || this.output === "both") {
            this.sendDirection(isRightOrForward ? "right" : "left");
          }
          if (this.output === "output" || this.output === "both") {
            const payload: DirectionControllerPayload = {
              label: effectiveLabels.start,
              index: isRightOrForward ? 1 : 0,
              timestamp: timestamp,
            };
            this.params.iostream.setOutputs({
              [DirectionControllerInputName]: payload,
            });
          }
        } else {
          this.params.iostream.setOutputs({
            MomentaryToggleSwitchVertical: isRightOrForward ? 1 : -1,
          });
          if (this.output === "controller" || this.output === "both") {
            this.sendDirection(isRightOrForward ? "forward" : "back");
          }
          if (this.output === "output" || this.output === "both") {
            const payload: DirectionControllerPayload = {
              label: effectiveLabels.start,
              index: isRightOrForward ? 2 : 3,
              timestamp: timestamp,
            };
            this.params.iostream.setOutputs({
              [DirectionControllerInputName]: payload,
            });
          }
        }
      } else {
        this.squareRotation = this.SQUARE_BASE_OFFSET + rotationDelta;
      }
    }

    if (this.isAnimating) {
      const elapsed = timestamp - this.animationStartTime;
      const duration = 500;

      if (elapsed < duration) {
        const progress = elapsed / duration;
        this.flashIntensity = Math.sin(progress * Math.PI);
      } else {
        this.isAnimating = false;
        this.flashIntensity = 0;
      }
    }

    if (!this.disableRendering) {
      this.resize();

      const gl = this.gl;
      const U = this.U;
      const P = this.PARAMS;

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.uniform2f(U.uResolution, this.canvas.width, this.canvas.height);
      gl.uniform1f(U.uSquareRotation, this.squareRotation);
      gl.uniform1f(U.uFlashIntensity, this.flashIntensity);

      gl.uniform1f(U.uCircleRadius, P.circleRadius ?? 0);
      gl.uniform1f(U.uCircleLineWidth, P.circleLineWidth ?? 0);
      gl.uniform1f(U.uCircleGlowWidth, P.circleGlowWidth ?? 0);
      gl.uniform1f(U.uCircleGlowIntensity, P.circleGlowIntensity ?? 0);

      gl.uniform1f(U.uSquareSize, P.squareSize ?? 0);
      gl.uniform1f(U.uSquareLineWidth, P.squareLineWidth ?? 0);
      gl.uniform1f(U.uStaticSquareLineWidth, P.staticSquareLineWidth ?? 0);
      gl.uniform1f(U.uSquareGlowWidth, P.squareGlowWidth ?? 0);
      gl.uniform1f(U.uSquareGlowIntensity, P.squareGlowIntensity ?? 0);

      gl.uniform1f(U.uInnerRadius, P.innerCircleRadius ?? 0);
      gl.uniform1f(U.uInnerLineWidth, P.innerCircleLineWidth ?? 0);
      gl.uniform1f(U.uInnerGlowWidth, P.innerCircleGlowWidth ?? 0);

      gl.uniform1f(U.uPatternRadius, P.patternCircleRadius ?? 0);
      gl.uniform1f(U.uPatternLineWidth, P.patternCircleLineWidth ?? 0);
      gl.uniform1f(U.uPatternGlowWidth, P.patternCircleGlowWidth ?? 0);

      gl.uniform1f(U.uGlowSharpness, P.glowSharpness ?? 0);
      gl.uniform1f(U.uAlphaMultiplier, P.alphaMultiplier ?? 0);
      gl.uniform1f(
        U.uArrowPointsRight,
        this.arrowPointsRight ? 1.0 : 0.0,
      );

      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    requestAnimationFrame((t) => this.render(t));
  }

  onInputs(inputs: CombinedResults): CombinedResults {
    // Skip all input processing when this axis is disabled
    if (!this.isEnabled) {
      return inputs;
    }

    if (inputs.wristRotationRight !== undefined) {
      this.wristRotationRight = inputs.wristRotationRight;
    }
    if (inputs.wristRotationLeft !== undefined) {
      this.wristRotationLeft = inputs.wristRotationLeft;
    }

    if (inputs.clench) {
      const clenchData = inputs.clench;
      const isClenchingRight = clenchData.isClenchRight;
      const isClenchingLeft = clenchData.isClenchLeft;

      const [leftClenchBox, rightClenchBox] = clenchData.handHitBoxes || [];

      const bothClenched = isClenchingRight && isClenchingLeft;
      const isClenching = !bothClenched &&
        ((isClenchingRight && this.hand === "right") ||
          (isClenchingLeft && this.hand === "left"));

      if (isClenching) {
        const wasInactive = this.activeHand === null;

        const activeBox = isClenchingRight ? rightClenchBox : leftClenchBox;
        const newActiveHand = isClenchingRight ? "right" : "left";

        if (wasInactive || newActiveHand !== this.activeHand) {
          this.activeHand = newActiveHand;
          this.baseRotation = this.effectiveWristRotation(this.activeHand);
          this.squareRotation = this.SQUARE_BASE_OFFSET;
          this.isCompleted = false;
          this.isAnimating = false;
          this.flashIntensity = 0;
        }

        if (!this.disableRendering) {
          this.effectDiv.style.display = "block";
          requestAnimationFrame(() => {
            this.effectDiv.style.opacity = "1";
          });

          if (activeBox) {
            const bounds = inputs.canvasBounds;
            const localBox = this.mapNormalizedBoxToLocalCoordinates(
              activeBox,
              bounds,
            );
            const size = Math.max(localBox.width, localBox.height);
            const centerX = localBox.x + localBox.width / 2;
            const centerY = localBox.y + localBox.height / 2;
            this.PARAMS.effectX = centerX;
            this.PARAMS.effectY = centerY;
            this.PARAMS.effectSize = size * 1.5;
            if (bounds != null) {
              this.PARAMS.effectViewportX = bounds.renderedX +
                (activeBox.x + activeBox.width / 2) * bounds.renderedWidth;
              this.PARAMS.effectViewportY = bounds.renderedY +
                (activeBox.y + activeBox.height / 2) * bounds.renderedHeight;
            } else {
              this.PARAMS.effectViewportX = null;
              this.PARAMS.effectViewportY = null;
            }
            this.updateLayout();
          }
        }
      } else {
        if (!this.disableRendering) {
          this.effectDiv.style.opacity = "0";
          setTimeout(() => {
            if (this.effectDiv.style.opacity === "0") {
              this.effectDiv.style.display = "none";
            }
          }, 300);
        }
        this.activeHand = null;
        this.baseRotation = 0;
        this.PARAMS.effectViewportX = null;
        this.PARAMS.effectViewportY = null;
      }
    }

    return inputs;
  }

  dispose(): void {
    while (this.disposers.length > 0) {
      this.disposers.pop()?.();
    }
    // Release WebGL context to prevent "Too many active WebGL contexts"
    this.gl.getExtension("WEBGL_lose_context")?.loseContext();
    this.root.remove();
  }
}
